import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import { aggregatorApi, AggregatorSettlement } from '../../api/aggregatorApi';
import {
  chartPalette,
  gridProps,
  axisTick,
  quietAxisProps,
  tooltipContentStyle,
  barRadius,
  MAX_BAR_SIZE,
  SERIES_FILL_OPACITY,
  activeBarProps,
  legendProps,
} from './chartTheme';

interface Props {
  dateFrom: string;
  dateTo: string;
}

const PALETTE = chartPalette;

const CommissionBurnChart: React.FC<Props> = ({ dateFrom, dateTo }) => {
  const { theme } = useTheme();
  const [settlements, setSettlements] = useState<AggregatorSettlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    aggregatorApi
      .settlements({ dateFrom, dateTo })
      .then((res) => {
        if (cancelled) return;
        setSettlements(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load settlements');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  const { rows, channels } = useMemo(() => {
    const channelSet = new Set<string>();
    const buckets = new Map<string, Record<string, number | string>>();
    settlements.forEach((s) => {
      if (!s.paymentDate) return;
      const ch = s.channelName || `Channel ${s.channelId}`;
      channelSet.add(ch);
      const key = s.paymentDate;
      const row = buckets.get(key) ?? { __key: key, __label: new Date(key).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) };
      row[ch] = ((row[ch] as number) || 0) + (s.commissionActual || 0);
      // running rate per row
      const gross = ((row.__gross as number) || 0) + (s.grossReported || 0);
      const com = ((row.__commission as number) || 0) + (s.commissionActual || 0);
      row.__gross = gross;
      row.__commission = com;
      row.ratePct = gross > 0 ? Number(((com / gross) * 100).toFixed(1)) : 0;
      buckets.set(key, row);
    });
    return {
      rows: Array.from(buckets.values()).sort((a, b) => String(a.__key).localeCompare(String(b.__key))),
      channels: Array.from(channelSet).sort(),
    };
  }, [settlements]);

  if (loading) {
    return (
      <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gray?.[400] }}>
        Loading…
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.danger }}>
        {error}
      </Box>
    );
  }
  if (rows.length === 0) {
    return (
      <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gray?.[400] }}>
        No aggregator settlements in this range
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={rows} margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="__label" tick={axisTick} {...quietAxisProps} />
        <YAxis
          yAxisId="left"
          tick={axisTick}
          tickFormatter={(v: number) => formatCurrency(v, { showDecimals: false, compact: true })}
          {...quietAxisProps}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={axisTick}
          tickFormatter={(v: number) => `${v}%`}
          domain={[0, 'auto']}
          {...quietAxisProps}
        />
        <Tooltip
          formatter={(value: number, name: string) =>
            name === 'Commission %' ? [`${value}%`, name] : [formatCurrency(value, { showDecimals: false }), name]
          }
          contentStyle={tooltipContentStyle}
        />
        <Legend {...legendProps} />
        {channels.map((c, i) => (
          <Bar
            key={c}
            yAxisId="left"
            dataKey={c}
            stackId="com"
            fill={PALETTE[i % PALETTE.length]}
            fillOpacity={SERIES_FILL_OPACITY}
            radius={i === channels.length - 1 ? barRadius : 0}
            maxBarSize={MAX_BAR_SIZE}
            {...activeBarProps}
          />
        ))}
        <Line yAxisId="right" type="monotone" dataKey="ratePct" name="Commission %" stroke={theme.warning || '#C9A961'} strokeWidth={2} dot={{ r: 3 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default CommissionBurnChart;
